import { checkAxiosResponse } from '@mst-fe/shared';
import { Form, Space, TreeSelect } from 'antd';
import axios from 'axios';
import defer from 'lodash/defer';
import startCase from 'lodash/startCase';
import { useEffect, useState } from 'react';

import { getAccessTokenFromCookie } from '../../../../../../services/auth';
import { FEED_FAMILIES_URL } from '../../../../../utils/midas-constants';
import { DataQueryFeed } from '../../../../types';

const ALWAYS_EXCLUDED_FEED_FAMILIES = ['cme'];

type SelectFeedControlParams = {
  excludeFamilies: string[];
  onSelectFamilyOrFeed: (value: string, option: DataQueryFeed) => void;
  showSelectFeedControl: boolean;
  selectFamily: boolean;
  selectFeed: boolean;
};

type SelectFeedControlFeeds = {
  tree: DataQueryFeed[];
  busy: boolean;
  error: unknown;
};

const DEFAULT_PROPS = {
  incomingExcludedFamilies: [],
  onSelectFamilyOrFeed: () => {},
};

export default function SelectFeedControl({
  excludeFamilies: incomingExcludedFamilies = DEFAULT_PROPS.incomingExcludedFamilies,
  onSelectFamilyOrFeed = DEFAULT_PROPS.onSelectFamilyOrFeed,
  showSelectFeedControl = true,
  selectFamily = true,
  selectFeed = true,
}: SelectFeedControlParams) {
  const token = getAccessTokenFromCookie();

  const [feeds, setFeeds] = useState<SelectFeedControlFeeds>({ tree: [], busy: false, error: undefined });

  useEffect(() => {
    setFeeds((s) => ({ ...s, busy: true, error: undefined }));

    defer(async () => {
      try {
        const rawTree = await checkAxiosResponse(
          axios.get<{
            [feed_family: string]: string[];
          }>(FEED_FAMILIES_URL, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: null,
          }),
          {
            errorMessage: 'Error while getting feeds',
          }
        );
        if (!rawTree) {
          throw new Error('Error while getting feeds');
        }

        const excludeFamilies = new Set([...incomingExcludedFamilies, ...ALWAYS_EXCLUDED_FEED_FAMILIES]);

        const tree: DataQueryFeed[] = [];

        for (const [feed_family, feeds] of Object.entries(rawTree)) {
          if (excludeFamilies.has(feed_family)) {
            continue;
          }

          tree.push({
            key: feed_family,
            value: feed_family,
            title: startCase(feed_family),
            feedFamily: feed_family,
            selectable: selectFamily,
            children: feeds.sort().map((feed) => ({
              key: `${feed_family}/${feed}`,
              value: `${feed_family}/${feed}`,
              title: feed,
              feedFamily: feed_family,
              selectable: selectFeed,
            })),
          });
        }

        setFeeds({ tree, busy: false, error: undefined });
      } catch (error: unknown) {
        console.error('[SelectFeedControl] failed to load sources from server..', error);
        setFeeds({ tree: [], busy: false, error });
      }
    });
  }, []);

  return (
    <Form.Item
      label="Feed"
      labelAlign="left"
      required
      help={feeds.error ? 'Failed to load feeds. Please try again later.' : undefined}
      validateStatus={feeds.error ? 'warning' : undefined}
    >
      <Space.Compact className="w-full">
        {showSelectFeedControl && (
          <Form.Item
            noStyle
            name="feed"
            rules={[{ required: true, message: 'Please select a feed.' }]}
            trigger="onChange"
            valuePropName="value"
            data-testid="data-query-feed"
          >
            <TreeSelect
              aria-label="Feed (Data Lake)"
              allowClear
              loading={feeds.busy}
              onSelect={onSelectFamilyOrFeed}
              placeholder="Please select a feed source"
              treeData={feeds.tree}
              data-testid="data-query-feed-tree"
            />
          </Form.Item>
        )}
      </Space.Compact>
    </Form.Item>
  );
}
